import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RedImmobile, RedGruppoImmobiliare, RedPraticaUSR } from '../../types/red'
import { FASE_CONFIG, EUR } from '../../constants/red'
import { redApi, praticheUsrApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import ExportXlsxButton from '../../components/red/ExportXlsxButton'

const RedPortfolio: React.FC = () => {
  const { user } = useAuth()
  const isSuperadmin = user?.role === 'superadmin'
  const [immobili, setImmobili] = useState<RedImmobile[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const navigate = useNavigate()

  const loadImmobili = () => {
    redApi.immobili.list()
      .then(res => {
        setImmobili(res.data)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        toast.error('Errore caricamento portfolio')
      })
  }

  useEffect(loadImmobili, [])

  if (loading) return <div className="p-8 text-gray-500">Caricamento portfolio...</div>

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Portfolio Immobiliare</h1>
          <p className="text-sm text-gray-500 mt-1">
            {immobili.length} immobili — Ricostruzione Marche
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isSuperadmin && (
            <>
              <ExportMasterButton />
              <ImportMasterButton onSuccess={loadImmobili} />
            </>
          )}
          <ExportXlsxButton large />
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            + Nuovo Immobile
          </button>
        </div>
      </div>

      {showCreate && (
        <NuovoImmobileModal
          onClose={() => setShowCreate(false)}
          onSuccess={(id) => {
            setShowCreate(false)
            toast.success('Immobile creato')
            navigate(`/red/${id}`)
          }}
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-6">
        {Object.entries(FASE_CONFIG).map(([fase, cfg]) => {
          const count = immobili.filter(i => i.fase_corrente === fase).length
          return (
            <div key={fase} style={{ backgroundColor: cfg.bg, borderColor: cfg.color }}
              className="rounded-lg border p-3 text-center">
              <div className="text-lg font-bold" style={{ color: cfg.color }}>{count}</div>
              <div className="text-xs text-gray-600 mt-1">{cfg.label}</div>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {immobili.map(imm => (
          <ImmobileCard key={imm.id} immobile={imm} onClick={() => navigate(`/red/${imm.id}`)} />
        ))}
      </div>

      {immobili.length === 0 && (
        <div className="text-center py-20 text-gray-400">
          <p className="text-lg">Nessun immobile nel portfolio</p>
        </div>
      )}
    </div>
  )
}

const ImmobileCard: React.FC<{ immobile: RedImmobile; onClick: () => void }> = ({ immobile, onClick }) => {
  const fase = FASE_CONFIG[immobile.fase_corrente]
  const completamento = immobile.calcoli?.completamento_documentale ?? 0
  const penale = immobile.calcoli?.penale_imeco_maturata

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-5 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 mr-3">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight">{immobile.nome_riferimento}</h3>
          {immobile.comune && (
            <p className="text-xs text-gray-500 mt-1">{immobile.comune} ({immobile.provincia})</p>
          )}
        </div>
        <span className="shrink-0 text-xs font-medium px-2 py-1 rounded-full"
          style={{ color: fase.color, backgroundColor: fase.bg }}>
          {fase.label}
        </span>
      </div>

      {immobile.numero_progressivo != null && (
        <div className="flex justify-center my-2">
          <div className="w-10 h-10 rounded-full border-2 border-blue-200 bg-blue-50 flex items-center justify-center">
            <span className="text-sm font-bold text-blue-700">{immobile.numero_progressivo}</span>
          </div>
        </div>
      )}

      <div className="mb-3 text-xs font-medium text-red-600 space-y-0.5">
        <p>
          {immobile.foglio
            ? `IMMOBILE F.${immobile.foglio} P.${immobile.particella} Sub.${immobile.subalterno || '—'}`
            : 'Dati catastali mancanti'}
        </p>
        {immobile.dati_catastali_extra?.map(v => (
          <p key={v.id}>
            {v.nome_voce.toUpperCase()} F.{v.foglio || '—'} P.{v.particella || '—'} Sub.{v.subalterno || '—'}
          </p>
        ))}
      </div>

      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Documenti</span>
          <span>{completamento}%</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${completamento}%`,
              backgroundColor: completamento === 100 ? '#10b981' : completamento > 50 ? '#3b82f6' : '#f59e0b',
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        {immobile.calcoli?.finanziamento_usr_totale != null && (
          <div className="bg-blue-50 rounded p-2">
            <div className="text-gray-500">Contributo USR</div>
            <div className="font-semibold text-blue-700">
              {EUR.format(immobile.calcoli.finanziamento_usr_totale)}
            </div>
          </div>
        )}
        {immobile.calcoli?.erogato_usr_a_oggi != null && (
          <div className="bg-green-50 rounded p-2">
            <div className="text-gray-500">Erogato USR</div>
            <div className="font-semibold text-green-700">
              {EUR.format(immobile.calcoli.erogato_usr_a_oggi)}
            </div>
          </div>
        )}
      </div>

      {penale != null && penale > 0 && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 font-medium">
          Penale maturata: {EUR.format(penale)}
        </div>
      )}

      {immobile.pratica_usr?.numero_pratica && (
        <div className="mt-2 text-xs text-gray-400">
          Pratica USR: {immobile.pratica_usr.numero_pratica}
        </div>
      )}
    </div>
  )
}

const NuovoImmobileModal: React.FC<{ onClose: () => void; onSuccess: (id: string) => void }> = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    nome_riferimento: '', indirizzo: '', comune: '', provincia: '', cap: '',
    foglio: '', particella: '', subalterno: '', categoria_catastale: '', superficie_mq: '',
  })
  const [saving, setSaving] = useState(false)
  const [privatoCondominio, setPrivatoCondominio] = useState<'privato' | 'condominio'>('privato')

  // Condominio
  const [condominiEsistenti, setCondominiEsistenti] = useState<RedGruppoImmobiliare[]>([])
  const [condoMode, setCondoMode] = useState<'nuovo' | 'esistente' | ''>('')
  const [condoSelezionatoId, setCondoSelezionatoId] = useState('')
  const [condoNome, setCondoNome] = useState('')
  const [condoImmobili, setCondoImmobili] = useState<string[]>([])
  const [condoImmobiliGruppo, setCondoImmobiliGruppo] = useState<RedImmobile[]>([])

  // Pratica USR
  const [praticheEsistenti, setPraticheEsistenti] = useState<RedPraticaUSR[]>([])
  const [praticaMode, setPraticaMode] = useState<'nuovo' | 'esistente' | ''>('')
  const [praticaSelezionataId, setPraticaSelezionataId] = useState('')
  const [condoPraticaNumero, setCondoPraticaNumero] = useState('')
  const [condoPraticaImporto, setCondoPraticaImporto] = useState('')
  const [condoQuota, setCondoQuota] = useState('')

  const [allImmobili, setAllImmobili] = useState<RedImmobile[]>([])
  const f = (k: keyof typeof form, v: string) => setForm(p => ({ ...p, [k]: v }))

  useEffect(() => {
    redApi.immobili.list().then(r => setAllImmobili(r.data.filter((i: RedImmobile) => !i.gruppo_id))).catch(() => {})
    redApi.gruppi.list().then(r => setCondominiEsistenti(r.data.filter((g: RedGruppoImmobiliare) => g.is_condominio))).catch(() => {})
  }, [])

  useEffect(() => {
    if (condoSelezionatoId && condoMode === 'esistente') {
      redApi.gruppi.listImmobili(condoSelezionatoId).then(r => setCondoImmobiliGruppo(r.data)).catch(() => {})
      praticheUsrApi.list(condoSelezionatoId).then(r => setPraticheEsistenti(r.data)).catch(() => {})
    } else {
      setCondoImmobiliGruppo([])
      setPraticheEsistenti([])
    }
  }, [condoSelezionatoId, condoMode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.nome_riferimento.trim()) {
      toast.error('Nome immobile obbligatorio')
      return
    }
    if (privatoCondominio === 'condominio') {
      if (!condoMode) {
        toast.error('Seleziona o crea un condominio')
        return
      }
      if (condoMode === 'nuovo' && !condoNome.trim()) {
        toast.error('Inserisci il nome del condominio')
        return
      }
      if (condoMode === 'esistente' && !condoSelezionatoId) {
        toast.error('Seleziona un condominio esistente')
        return
      }
      if (praticaMode === 'esistente' && !praticaSelezionataId) {
        toast.error('Seleziona una pratica USR esistente')
        return
      }
      if (praticaMode === 'nuovo' && !condoPraticaNumero.trim()) {
        toast.error('Inserisci il numero della pratica USR')
        return
      }
    }
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(form)) {
        payload[k] = v === '' ? null : v
      }
      payload.privato_o_condominio = privatoCondominio
      const res = await redApi.immobili.create(payload)
      const newId = res.data.id

      if (privatoCondominio === 'condominio') {
        if (condoMode === 'nuovo' && condoNome) {
          await redApi.gruppi.creaCondominio({
            nome_condominio: condoNome,
            immobile_ids: condoImmobili,
            pratica_usr: condoPraticaNumero ? {
              numero_pratica: condoPraticaNumero,
              importo_approvato: condoPraticaImporto ? parseFloat(condoPraticaImporto) : null,
            } : null,
            immobile_corrente_id: newId,
            immobile_corrente_quota: condoQuota ? parseFloat(condoQuota) : null,
          })
        } else if (condoMode === 'esistente' && condoSelezionatoId) {
          const updatePayload: Record<string, unknown> = { gruppo_id: condoSelezionatoId }
          if (praticaMode === 'esistente' && praticaSelezionataId) {
            updatePayload.pratica_usr_id = praticaSelezionataId
            updatePayload.usr_quota_percentuale = condoQuota ? parseFloat(condoQuota) : null
          } else if (praticaMode === 'nuovo' && condoPraticaNumero) {
            const praticaRes = await praticheUsrApi.create({
              gruppo_id: condoSelezionatoId,
              numero_pratica: condoPraticaNumero,
              importo_approvato: condoPraticaImporto ? parseFloat(condoPraticaImporto) : null,
            })
            updatePayload.pratica_usr_id = praticaRes.data.id
            updatePayload.usr_quota_percentuale = condoQuota ? parseFloat(condoQuota) : null
          }
          await redApi.immobili.update(newId, updatePayload)
        }
      }

      onSuccess(newId)
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Errore creazione immobile')
      // Nota: se l'immobile è stato creato ma la creazione del condominio/pratica è fallita,
      // l'immobile resta nel database senza gruppo. L'utente può associarlo manualmente.
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Nuovo Immobile</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="label">Tipologia</label>
            <select
              className="input"
              value={privatoCondominio}
              onChange={e => {
                setPrivatoCondominio(e.target.value as 'privato' | 'condominio')
                setCondoMode('')
                setCondoSelezionatoId('')
                setPraticaMode('')
                setPraticaSelezionataId('')
              }}
            >
              <option value="privato">Privato</option>
              <option value="condominio">Condominio</option>
            </select>
          </div>

          {privatoCondominio === 'condominio' && (
            <div className="bg-blue-50 rounded-lg p-4 space-y-3">
              <div>
                <label className="label">Condominio</label>
                <select
                  className="input"
                  value={condoMode}
                  onChange={e => {
                    setCondoMode(e.target.value as 'nuovo' | 'esistente' | '')
                    setCondoSelezionatoId('')
                    setCondoNome('')
                    setPraticaMode('')
                    setPraticaSelezionataId('')
                    setCondoImmobili([])
                  }}
                >
                  <option value="">-- Seleziona --</option>
                  <option value="nuovo">+ Crea nuovo condominio</option>
                  <option value="esistente">Seleziona condominio esistente</option>
                </select>
              </div>

              {condoMode === 'nuovo' && (
                <div>
                  <label className="label">Nome condominio</label>
                  <input className="input" value={condoNome} onChange={e => setCondoNome(e.target.value)} placeholder="Condominio Via Roma 10" />
                </div>
              )}

              {condoMode === 'esistente' && (
                <div>
                  <label className="label">Seleziona condominio</label>
                  <select
                    className="input"
                    value={condoSelezionatoId}
                    onChange={e => {
                      setCondoSelezionatoId(e.target.value)
                      setPraticaMode('')
                      setPraticaSelezionataId('')
                    }}
                  >
                    <option value="">-- Seleziona --</option>
                    {condominiEsistenti.map(c => (
                      <option key={c.id} value={c.id}>{c.nome_gruppo} ({c.n_immobili} imm.)</option>
                    ))}
                  </select>
                </div>
              )}

              {condoMode === 'nuovo' && (
                <div>
                  <label className="label">Immobili da raggruppare</label>
                  <div className="max-h-32 overflow-y-auto bg-white rounded border p-2 space-y-1">
                    {allImmobili.map(i => (
                      <label key={i.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={condoImmobili.includes(i.id)}
                          onChange={e => {
                            setCondoImmobili(e.target.checked
                              ? [...condoImmobili, i.id]
                              : condoImmobili.filter(id => id !== i.id))
                          }}
                        />
                        <span>{i.nome_riferimento} — {i.comune}</span>
                      </label>
                    ))}
                    {allImmobili.length === 0 && <p className="text-xs text-gray-400">Nessun immobile disponibile</p>}
                  </div>
                </div>
              )}

              {condoMode === 'esistente' && condoSelezionatoId && condoImmobiliGruppo.length > 0 && (
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-500 mb-1">Immobili già nel condominio:</p>
                  <div className="space-y-1">
                    {condoImmobiliGruppo.map(i => (
                      <div key={i.id} className="text-sm text-gray-700">• {i.nome_riferimento} — {i.comune}</div>
                    ))}
                  </div>
                </div>
              )}

              {condoMode && (
                <>
                  <div>
                    <label className="label">Pratica USR</label>
                    <select
                      className="input"
                      value={praticaMode}
                      onChange={e => {
                        setPraticaMode(e.target.value as 'nuovo' | 'esistente' | '')
                        setPraticaSelezionataId('')
                      }}
                    >
                      <option value="">-- Seleziona --</option>
                      {condoMode === 'esistente' && praticheEsistenti.length > 0 && (
                        <option value="esistente">Seleziona pratica esistente</option>
                      )}
                      <option value="nuovo">+ Crea nuova pratica USR</option>
                    </select>
                  </div>

                  {praticaMode === 'esistente' && (
                    <div>
                      <select
                        className="input"
                        value={praticaSelezionataId}
                        onChange={e => setPraticaSelezionataId(e.target.value)}
                      >
                        <option value="">-- Seleziona pratica --</option>
                        {praticheEsistenti.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.numero_pratica} {p.importo_approvato ? `(€${Number(p.importo_approvato).toLocaleString()})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {praticaMode === 'nuovo' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="label text-xs">Numero pratica USR</label>
                        <input className="input" value={condoPraticaNumero} onChange={e => setCondoPraticaNumero(e.target.value)} placeholder="USR-2024-001" />
                      </div>
                      <div>
                        <label className="label text-xs">Importo approvato (€)</label>
                        <input type="number" className="input" value={condoPraticaImporto} onChange={e => setCondoPraticaImporto(e.target.value)} placeholder="0,00" />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="label text-xs">Quota % immobile corrente</label>
                    <input type="number" step="0.01" className="input" value={condoQuota} onChange={e => setCondoQuota(e.target.value)} placeholder="%" />
                  </div>
                </>
              )}
            </div>
          )}

          <div>
            <label className="label">Nome immobile *</label>
            <input className="input" value={form.nome_riferimento} onChange={e => f('nome_riferimento', e.target.value)} required autoFocus />
          </div>
          <div><label className="label">Indirizzo</label><input className="input" value={form.indirizzo} onChange={e => f('indirizzo', e.target.value)} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><label className="label">Comune</label><input className="input" value={form.comune} onChange={e => f('comune', e.target.value)} /></div>
            <div><label className="label">Provincia</label><input className="input" value={form.provincia} onChange={e => f('provincia', e.target.value)} /></div>
            <div><label className="label">CAP</label><input className="input" value={form.cap} onChange={e => f('cap', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><label className="label">Foglio</label><input className="input" value={form.foglio} onChange={e => f('foglio', e.target.value)} /></div>
            <div><label className="label">Particella</label><input className="input" value={form.particella} onChange={e => f('particella', e.target.value)} /></div>
            <div><label className="label">Subalterno</label><input className="input" value={form.subalterno} onChange={e => f('subalterno', e.target.value)} /></div>
            <div><label className="label">Categoria</label><input className="input" value={form.categoria_catastale} onChange={e => f('categoria_catastale', e.target.value)} /></div>
          </div>
          <div><label className="label">Superficie (mq)</label><input type="number" className="input" value={form.superficie_mq} onChange={e => f('superficie_mq', e.target.value)} /></div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? 'Creazione...' : 'Crea immobile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const ExportMasterButton: React.FC = () => {
  const [exporting, setExporting] = useState(false)

  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await redApi.export.master()
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `RED_MASTER_IMMOBILI_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('Errore durante l\'export')
    } finally {
      setExporting(false)
    }
  }

  return (
    <button
      onClick={handleExport}
      disabled={exporting}
      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5"
    >
      {exporting ? (
        <>
          <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          Export...
        </>
      ) : (
        <>
          📊 Export Master
        </>
      )}
    </button>
  )
}

const ImportMasterButton: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const [importing, setImporting] = useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)
    try {
      const res = await redApi.importMaster(file)
      const data = res.data
      if (data.errori?.length > 0) {
        toast.error(`Importati ${data.creati}, errori: ${data.errori.length}`)
        console.warn('Import errori:', data.errori)
      } else {
        toast.success(`Import master completato: ${data.creati} creati, ${data.aggiornati} aggiornati`)
      }
      onSuccess()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Errore import')
    } finally {
      setImporting(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleFile}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={importing}
        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
      >
        {importing ? (
          <>
            <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Import...
          </>
        ) : (
          <>📥 Import Master</>
        )}
      </button>
    </>
  )
}

export default RedPortfolio
